import { apiClient } from "@/api/api-client";
import { z } from "zod";
import { isAxiosError } from "axios";
import type { ContractStatus } from "@/lib/types/contracts";
import type {
  PaginatedTests,
  SubmitTestPayload,
  SubmitTestResponse,
  ModerateTestPayload,
  ModerationResponse,
  ValidateTestPayload,
  ValidationResponse,
  TestDefinition,
  TestImportDefinition,
} from "@/lib/types/tests";

export type ReadyFinalValidationContractStatus =
  | ContractStatus.DRAFT
  | ContractStatus.SENT
  | ContractStatus.VIEWED
  | ContractStatus.SIGNED
  | ContractStatus.FINAL_VALIDATED;

export type ContributorTestLanguageEligibility = {
  language: string;
  totalAudioContributions: number;
  validatedAudioCount: number;
  rejectedAudioCount: number;
  remaining: number;
  progress: number;
  eligible: boolean;
};

export type ContributorTestEligibility = {
  threshold: number;
  totalAudioContributions: number;
  validatedAudioCount: number;
  rejectedAudioCount: number;
  eligible: boolean;
  validationRule: {
    minimumValidationScore: number;
    moderatorValidationWeight: number;
    validationsMustExceedRejections: boolean;
  };
  languages: ContributorTestLanguageEligibility[];
};

export type TestSubmissionTrashItem = {
  id: string;
  userId: string;
  language: string;
  status: string;
  deletedAt: string;
  deletedBy: string;
  deletionReason: string;
  restoreUntil: string;
  recordVersion: number;
};

export type TestSubmissionTrashPage = {
  items: TestSubmissionTrashItem[];
  nextCursor: string | null;
};

const trashItemSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().min(1),
  language: z.string().min(1),
  status: z.string().min(1),
  deletedAt: z.string().datetime(),
  deletedBy: z.string().min(1),
  deletionReason: z.string().min(1),
  restoreUntil: z.string().datetime(),
  recordVersion: z.number().int().nonnegative(),
}).strict();
const trashListSchema = z.object({
  items: z.array(trashItemSchema).max(100),
  nextCursor: z.string().nullable(),
}).strict();
const trashResultSchema = z.object({
  message: z.literal("TEST_SUBMISSION_TRASHED"),
  restoreUntil: z.string().datetime(),
}).strict();
const restoreResultSchema = z.object({ message: z.literal("TEST_SUBMISSION_RESTORED") }).strict();

function requireTrashItem(item: z.infer<typeof trashItemSchema>): TestSubmissionTrashItem {
  if (
    typeof item.id !== "string" ||
    typeof item.userId !== "string" ||
    typeof item.language !== "string" ||
    typeof item.status !== "string" ||
    typeof item.deletedAt !== "string" ||
    typeof item.deletedBy !== "string" ||
    typeof item.deletionReason !== "string" ||
    typeof item.restoreUntil !== "string" ||
    typeof item.recordVersion !== "number"
  ) {
    throw new Error("INVALID_TEST_SUBMISSION_TRASH_RESPONSE");
  }

  return {
    id: item.id,
    userId: item.userId,
    language: item.language,
    status: item.status,
    deletedAt: item.deletedAt,
    deletedBy: item.deletedBy,
    deletionReason: item.deletionReason,
    restoreUntil: item.restoreUntil,
    recordVersion: item.recordVersion,
  };
}

function parseTrashPage(value: unknown): TestSubmissionTrashPage {
  const page = trashListSchema.parse(value);
  if (!Array.isArray(page.items)) throw new Error("INVALID_TEST_SUBMISSION_TRASH_RESPONSE");
  if (page.nextCursor !== null && typeof page.nextCursor !== "string") {
    throw new Error("INVALID_TEST_SUBMISSION_TRASH_RESPONSE");
  }
  return { items: page.items.map(requireTrashItem), nextCursor: page.nextCursor };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const normalizeSubmissionsResponse = (data: unknown, fallbackPage: number, fallbackLimit: number) => {
  const record = isRecord(data) ? data : {};
  const pagination = isRecord(record.pagination) ? record.pagination : {};
  const items = Array.isArray(record.items) ? record.items : [];
  const pageValue = record.page ?? pagination.page;
  const limitValue = record.limit ?? pagination.pageSize;
  const totalValue = record.total ?? pagination.total;
  const page = typeof pageValue === "number" && Number.isSafeInteger(pageValue) && pageValue > 0 ? pageValue : fallbackPage;
  const limit = typeof limitValue === "number" && Number.isSafeInteger(limitValue) && limitValue > 0 ? limitValue : fallbackLimit;
  const total = typeof totalValue === "number" && Number.isSafeInteger(totalValue) && totalValue >= 0 ? totalValue : items.length;
  const totalPagesValue = record.totalPages;
  const totalPages = typeof totalPagesValue === "number" && Number.isSafeInteger(totalPagesValue) && totalPagesValue >= 0
    ? totalPagesValue
    : Math.max(1, Math.ceil(total / limit));

  return {
    page,
    limit,
    total,
    totalPages,
    contractStatus: typeof record.contractStatus === "string" ? record.contractStatus : undefined,
    items,
  };
};

const contributorSubmissionResponseSchema = z.object({
  message: z.literal("TEST_SUBMITTED"),
  submissionId: z.string().uuid(),
}).strict();

const contributorSubmissionFailureSchema = z.object({
  error: z.literal(true),
  status: z.literal(409),
  message: z.enum(["SUBMISSION_IN_PROGRESS", "IDEMPOTENCY_KEY_REUSED"]),
  data: z.null(),
  meta: z.object({
    request_id: z.string(),
    served_at: z.string(),
    retryable: z.boolean(),
  }).strict(),
}).strict();

export function contributorSubmissionFailureCode(error: unknown) {
  if (!isAxiosError(error)) return null;
  const parsed = contributorSubmissionFailureSchema.safeParse(error.response?.data);
  return parsed.success ? parsed.data.message : null;
}

export const testsApi = {
  async trashSubmission(id: string, reason: string, expectedVersion: number) {
    const { data } = await apiClient.post<unknown>(`/tests/submissions/${id}/trash`, {
      reason,
      expectedVersion,
    });
    return trashResultSchema.parse(data);
  },

  async listTrashedSubmissions(limit = 50, cursor?: string): Promise<TestSubmissionTrashPage> {
    const { data } = await apiClient.get<unknown>(`/tests/submissions/trash`, {
      params: { limit, ...(cursor ? { cursor } : {}) },
    });
    return parseTrashPage(data);
  },

  async restoreSubmission(id: string) {
    const { data } = await apiClient.post<unknown>(`/tests/submissions/${id}/restore`, {});
    return restoreResultSchema.parse(data);
  },

  async getContributorEligibility() {
    const { data } = await apiClient.get<ContributorTestEligibility>(
      `/tests/eligibility`,
    );
    return data;
  },

  async list(params?: { page?: number; pageSize?: number; language?: string; status?: string }) {
    const { data } = await apiClient.get<PaginatedTests>(`/tests`, { params });
    return data;
  },

  async getDefinition(testId: string) {
    const { data } = await apiClient.get<TestDefinition>(`/tests/${testId}`);
    return data;
  },

  async submit(payload: SubmitTestPayload) {
    const { data } = await apiClient.post<SubmitTestResponse>(`/tests/submit`, payload);
    return data;
  },

  async moderate(testId: string, payload: ModerateTestPayload) {
    const { data } = await apiClient.patch<ModerationResponse>(`/tests/${testId}/moderate`, payload);
    return data;
  },

  async validate(testId: string, payload: ValidateTestPayload) {
    const { data } = await apiClient.patch<ValidationResponse>(`/tests/${testId}/validate`, payload);
    return data;
  },

  async createDefinition(payload: TestImportDefinition) {
    const { data } = await apiClient.post(`/tests/definition`, payload);
    return data;
  },

  async getRandomDefinitionsByLang(lang: string) {
    const { data } = await apiClient.get(`/tests/random`, { params: { lang } });
    return data;
  },

  async submitContributorTest(payload: {
    language: string;
    answers: {
      phonemes: string[];
      phrases: { local: string; french?: string; userAnswer?: string }[];
    };
  }, idempotencyKey: string) {
    const { data } = await apiClient.post(`/tests/submit`, payload, {
      headers: { "Idempotency-Key": idempotencyKey },
    });
    return contributorSubmissionResponseSchema.parse(data);
  },

  async submitContributorTestWithAudio(form: FormData, idempotencyKey: string) {
    const { data } = await apiClient.post(`/tests/submit-audio`, form, {
      headers: {
        "Content-Type": "multipart/form-data",
        "Idempotency-Key": idempotencyKey,
      },
    });
    return contributorSubmissionResponseSchema.parse(data);
  },

  async getMySubmissions(status?: string) {
    const { data } = await apiClient.get(`/tests/my-submissions`, {
      params: { ...(status ? { status } : {}) },
    });
    return { items: Array.isArray(data?.items) ? data.items : [] };
  },

  async getPendingSubmissions(page = 1, limit = 10) {
    const { data } = await apiClient.get(`/tests/pending`, { params: { page, limit } });
    return normalizeSubmissionsResponse(data, page, limit);
  },

  async getValidatedSubmissions(page = 1, limit = 10) {
    const { data } = await apiClient.get(`/tests/validated`, { params: { page, limit } });
    return normalizeSubmissionsResponse(data, page, limit);
  },

  async getCorrectedSubmissions(page = 1, limit = 10) {
    const { data } = await apiClient.get(`/tests/corrected`, { params: { page, limit } });
    return normalizeSubmissionsResponse(data, page, limit);
  },

  async getSubmissionsReadyForFinalValidation(page = 1, limit = 10, status?: ReadyFinalValidationContractStatus) {
    const { data } = await apiClient.get(`/tests/ready-final-validation`, {
      params: { page, limit, ...(status ? { status } : {}) },
    });
    return normalizeSubmissionsResponse(data, page, limit);
  },

  async moderateSubmission(
    id: string,
    payload:
      | { status?: 'PENDING' | 'VALIDATED' | 'REJECTED'; score?: number; reason?: string; correctionStatus?: 'TO_CORRECT' | 'CORRECTED' }
      | 'VALIDATED'
      | 'REJECTED',
    score?: number
  ) {
    const body = typeof payload === 'string'
      ? { status: payload, score }
      : payload;
    const { data } = await apiClient.patch(`/tests/moderate/${id}`, body);
    return data as { message: string };
  },
};

